/**
 * @file RightPanelContext
 * @description Unified context for managing right-side panel state in Patient360.
 *
 * Usage:
 *   const { openPanel, closePanel, state } = useRightPanel();
 *   openPanel('CREATE_CASE', {});
 */

import { createContext, useContext, useCallback, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { PanelType, PanelConfig, PanelState } from './types';

// ─── Context Shape ───────────────────────────────────────────

interface RightPanelContextValue {
  state: PanelState;
  openPanel: <T extends PanelConfig>(type: T['type'], payload: T['payload']) => void;
  closePanel: () => void;
}

const INITIAL_STATE: PanelState = {
  isOpen: false,
  type: null,
  payload: null,
};

const RightPanelContext = createContext<RightPanelContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────

interface RightPanelProviderProps {
  children: ReactNode;
}

export function RightPanelProvider({ children }: RightPanelProviderProps) {
  const [state, setState] = useState<PanelState>(INITIAL_STATE);

  const openPanel = useCallback(<T extends PanelConfig>(type: T['type'], payload: T['payload']) => {
    setState({ isOpen: true, type: type as PanelType, payload });
  }, []);

  const closePanel = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const value = useMemo(
    () => ({ state, openPanel, closePanel }),
    [state, openPanel, closePanel],
  );

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────

export function useRightPanel(): RightPanelContextValue {
  const ctx = useContext(RightPanelContext);
  if (!ctx) {
    throw new Error('useRightPanel must be used within a RightPanelProvider');
  }
  return ctx;
}
